import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FileText,
  CheckCircle2,
  XCircle,
  Receipt,
  ClipboardList,
  FolderPlus,
  UserPlus,
  GitMerge,
  FileDown,
  Phone,
  Loader2,
  Ban,
  RotateCcw,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { getActivityItemPdfUrl } from '@/lib/api/v2/activity'
import type { ActivityItemDto, ActivityEventType } from '@/types'

// ── Event metadata ────────────────────────────────────────────────────────────

interface EventMeta {
  label: string
  icon: React.ComponentType<{ className?: string }>
  color: string
  iconColor: string
}

const EVENT_META: Record<ActivityEventType, EventMeta> = {
  quote_generated: {
    label: 'Devis généré',
    icon: FileText,
    color: 'bg-slate-100',
    iconColor: 'text-slate-600',
  },
  quote_accepted: {
    label: 'Devis accepté',
    icon: CheckCircle2,
    color: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  quote_declined: {
    label: 'Devis décliné',
    icon: XCircle,
    color: 'bg-red-100',
    iconColor: 'text-red-500',
  },
  quote_follow_up: {
    label: 'Relance client',
    icon: Phone,
    color: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  invoice_deposit: {
    label: 'Acompte',
    icon: Receipt,
    color: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
  invoice_final: {
    label: 'Facture finale',
    icon: Receipt,
    color: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
  invoice_standalone: {
    label: 'Facture supplémentaire',
    icon: Receipt,
    color: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
  invoice_credit_note: {
    label: 'Note de crédit',
    icon: Receipt,
    color: 'bg-red-100',
    iconColor: 'text-red-600',
  },
  // SOLY-ORDER-002 — the sale lifecycle narrated (the order's audit columns hold only the CURRENT
  // cancellation; the feed is the history). Slate = factual state, like the « Vente annulée » badge.
  sale_cancelled: {
    label: 'Vente annulée',
    icon: Ban,
    color: 'bg-slate-100',
    iconColor: 'text-slate-600',
  },
  sale_reopened: {
    label: 'Vente réactivée',
    icon: RotateCcw,
    color: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  work_order_generated: {
    label: 'Bordereau généré',
    icon: ClipboardList,
    color: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
  },
  project_created: {
    label: 'Projet créé',
    icon: FolderPlus,
    color: 'bg-zinc-100',
    iconColor: 'text-zinc-600',
  },
  client_created: {
    label: 'Client créé',
    icon: UserPlus,
    color: 'bg-zinc-100',
    iconColor: 'text-zinc-600',
  },
  client_merged: {
    label: 'Fiches client fusionnées',
    icon: GitMerge,
    color: 'bg-zinc-100',
    iconColor: 'text-zinc-600',
  },
}

function getEventMeta(eventType: string): EventMeta {
  return (
    EVENT_META[eventType as ActivityEventType] ?? {
      label: eventType,
      icon: FileText,
      color: 'bg-zinc-100',
      iconColor: 'text-zinc-500',
    }
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatAmount(amount: number): string {
  return amount.toLocaleString('fr-BE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  })
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMinutes < 1) return "à l'instant"
  if (diffMinutes < 60) return `il y a ${diffMinutes} min`
  if (diffHours < 24) return `il y a ${diffHours}h`
  if (diffDays === 1) return 'hier'
  if (diffDays < 7) return `il y a ${diffDays}j`
  return date.toLocaleDateString('fr-BE', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ── PdfButton ─────────────────────────────────────────────────────────────────

function PdfButton({ id }: { id: string }) {
  const [loading, setLoading] = useState(false)

  const handleClick = async () => {
    if (loading) return
    setLoading(true)
    try {
      const { url } = await getActivityItemPdfUrl(id)
      window.open(url, '_blank', 'noopener,noreferrer')
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className="shrink-0 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50"
      title="Télécharger le PDF"
    >
      {loading
        ? <Loader2 className="h-3 w-3 animate-spin" />
        : <FileDown className="h-3 w-3" />
      }
      PDF
    </button>
  )
}

// ── ActivityItem ──────────────────────────────────────────────────────────────

interface ActivityItemProps {
  item: ActivityItemDto
  isLast: boolean
}

function ActivityItem({ item, isLast }: ActivityItemProps) {
  const navigate = useNavigate()
  const meta = getEventMeta(item.eventType)
  const Icon = meta.icon

  return (
    <div className="flex gap-2.5 py-2">
      {/* Timeline: icon + connector */}
      <div className="flex flex-col items-center shrink-0">
        <div className={cn('flex h-6 w-6 items-center justify-center rounded-full', meta.color)}>
          <Icon className={cn('h-3.5 w-3.5', meta.iconColor)} />
        </div>
        {!isLast && <div className="mt-1 w-px flex-1 bg-border" />}
      </div>

      {/* Content — single dense row */}
      <div className={cn('flex min-w-0 flex-1 items-baseline gap-1.5 flex-wrap', isLast ? 'pb-0' : 'pb-2')}>
        {/* Event label */}
        <span className="text-sm text-foreground whitespace-nowrap">{meta.label}</span>

        {/* Linked data inline */}
        {item.documentNumber && item.projectId && (
          <button
            type="button"
            onClick={() => navigate(`/projects/${item.projectId}`)}
            className="text-sm font-medium text-foreground hover:underline whitespace-nowrap"
          >
            {item.documentNumber}
          </button>
        )}
        {item.documentNumber && !item.projectId && (
          <span className="text-sm font-medium text-foreground">{item.documentNumber}</span>
        )}

        {item.clientName && item.customerId && (
          <button
            type="button"
            onClick={() => navigate(`/clients/${item.customerId}`)}
            className="text-sm text-muted-foreground hover:underline hover:text-foreground whitespace-nowrap transition-colors"
          >
            {item.clientName}
          </button>
        )}
        {item.clientName && !item.customerId && (
          <span className="text-sm text-muted-foreground">{item.clientName}</span>
        )}

        {!item.documentNumber && item.projectReference && item.projectId && (
          <button
            type="button"
            onClick={() => navigate(`/projects/${item.projectId}`)}
            className="text-sm text-muted-foreground hover:underline hover:text-foreground whitespace-nowrap transition-colors"
          >
            {item.projectReference}
          </button>
        )}

        {item.amount !== null && item.amount !== undefined && (
          <span className="text-sm text-muted-foreground whitespace-nowrap">{formatAmount(item.amount)}</span>
        )}

        {item.extraContext && (
          <span className="text-sm text-muted-foreground">{item.extraContext}</span>
        )}

        {/* Spacer */}
        <span className="flex-1" />

        {/* PDF · time · author — right side */}
        {item.hasPdf && <PdfButton id={item.id} />}
        <span className="text-xs text-muted-foreground whitespace-nowrap">{formatRelativeTime(item.occurredAt)}</span>
        <span className="text-xs text-muted-foreground/70 whitespace-nowrap">{item.actorName}</span>
      </div>
    </div>
  )
}

// ── ActivityFeed ──────────────────────────────────────────────────────────────

interface ActivityFeedProps {
  items: ActivityItemDto[]
  loading?: boolean
  emptyMessage?: string
  className?: string
}

export function ActivityFeed({
  items,
  loading = false,
  emptyMessage = 'Aucune activité sur cette période',
  className,
}: ActivityFeedProps) {
  if (loading) {
    return (
      <div className={cn('space-y-2', className)}>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex gap-2.5 animate-pulse">
            <div className="h-6 w-6 shrink-0 rounded-full bg-muted" />
            <div className="flex-1 flex items-center gap-2 pt-0.5">
              <div className="h-3 w-24 rounded bg-muted" />
              <div className="h-3 w-32 rounded bg-muted" />
              <div className="h-3 w-16 rounded bg-muted" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <p className={cn('text-sm text-muted-foreground', className)}>
        {emptyMessage}
      </p>
    )
  }

  return (
    <div className={cn('', className)}>
      {items.map((item, index) => (
        <ActivityItem key={item.id} item={item} isLast={index === items.length - 1} />
      ))}
    </div>
  )
}
